import { useMemo } from "react";

import { getInitialsFromFullName } from "../../../../constants/constants";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { useGetEmailTemplatesQuery } from "../../../../slices/EmailTemplatesSlice";
import { useGetStatusesQuery } from "../../../../slices/StatusesSlice";
import { useGetUsersQuery } from "../../../../slices/UserSlice";
import { useGetWorkTypeQuery } from "../../../../slices/WorkTypeSlice";
import { SelectOptionType } from "../../../../types/types";
import { desiredOrder } from "../../../dashboard/Dashboard";
import { AutomationLookupOptions } from "./automation.constants";

export type AutomationStatusOption = SelectOptionType & {
  statusKind: "main" | "sub";
  mainStatusId?: string;
  subStatusId?: string;
};

const DATE_RULE_OPTIONS: SelectOptionType[] = [
  {
    label: "day(s) before due date",
    value: "before",
  },
  {
    label: "day(s) after start date",
    value: "after",
  },
];

const getStatusOrder = (statusName?: string) => {
  if (!statusName) {
    return Number.MAX_SAFE_INTEGER;
  }

  const index = desiredOrder.indexOf(statusName as typeof desiredOrder[number]);

  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const uniqueByName = <T extends { subStatus?: string | null }>(
  items: T[],
): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = (item?.subStatus || "").trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const useAutomationLookups = (workTypeId?: string) => {
  const orgId = getLinkedFirm()?.orgId || "";

  const { data: users, isFetching: isLoadingUsers } = useGetUsersQuery(orgId, {
    skip: !orgId,
  });

  const { data: workType, isFetching: isLoadingStatuses } = useGetWorkTypeQuery(
    { id: workTypeId || "" },
    {
      skip: !workTypeId,
    },
  );
  const { data: orgStatuses, isFetching: isLoadingOrgStatuses } =
    useGetStatusesQuery(
      { orgId },
      {
        skip: !orgId || Boolean(workTypeId),
      },
    );

  const { data: emailTemplates, isFetching: isLoadingEmailTemplates } =
    useGetEmailTemplatesQuery(
      { orgId },
      {
        skip: !orgId,
      },
    );

  const usersOptions = useMemo<SelectOptionType[]>(() => {
    return (
      users?.map((item) => {
        const firstName = item?.userProfile?.firstName || "";
        const lastName = item?.userProfile?.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          avatar: getInitialsFromFullName(fullName),
          avatarCustomColor: item?.userProfile?.color,
          label: fullName,
          value: `${item?.id}`,
        };
      }) || []
    );
  }, [users]);

  const statusesOptions = useMemo<AutomationStatusOption[]>(() => {
    if (!workTypeId) {
      return [...(orgStatuses ?? [])]
        .filter((status) => status?.name !== "trial")
        .sort((a, b) => getStatusOrder(a?.name) - getStatusOrder(b?.name))
        .flatMap<AutomationStatusOption>((item) => [
          {
            label: item?.name,
            value: item?.id,
            color: item?.color,
            isBold: true,
            statusKind: "main",
            mainStatusId: item?.id,
          },
          // The firm list repeats a sub-status name once per work type that uses
          // it (QA batch 14, AUTO-017): show each name once. Copies to a work
          // item are matched to its work type's statuses by name on the server.
          ...uniqueByName(item?.workTypeSubStatuses || []).map(
            (subItem): AutomationStatusOption => ({
              label: subItem?.subStatus,
              value: subItem?.id,
              color: item?.color,
              statusKind: "sub",
              mainStatusId: item?.id,
              subStatusId: subItem?.id,
            }),
          ),
        ]);
    }

    return [...(workType?.serviceStatuses ?? [])]
      .filter((status) => status?.name !== "trial")
      .sort((a, b) => getStatusOrder(a?.name) - getStatusOrder(b?.name))
      .flatMap<AutomationStatusOption>((item) => [
        {
          label: item?.name,
          value: item?.id,
          color: item?.color,
          isBold: true,
          statusKind: "main",
          mainStatusId: item?.id,
        },
        ...(item?.serviceSubStatuses || []).map(
          (subItem): AutomationStatusOption => ({
            label: subItem?.subStatus,
            value: subItem?.id,
            color: item?.color,
            statusKind: "sub",
            mainStatusId: item?.id,
            subStatusId: subItem?.id,
          }),
        ),
      ]);
  }, [orgStatuses, workType?.serviceStatuses, workTypeId]);

  const emailTemplateOptions = useMemo<SelectOptionType[]>(() => {
    return (
      emailTemplates?.map((template) => ({
        label: template.name,
        value: template.id,
      })) || []
    );
  }, [emailTemplates]);

  const options: AutomationLookupOptions = {
    workStatuses: statusesOptions,
    subtaskStatuses: statusesOptions,
    users: usersOptions,
    teamMembers: usersOptions,
    emailTemplates: emailTemplateOptions,
    dateRules: DATE_RULE_OPTIONS,
  };

  return {
    options,

    isLoadingUsers,
    isLoadingStatuses: isLoadingStatuses || isLoadingOrgStatuses,
    isLoadingEmailTemplates,
    isLoading:
      isLoadingUsers ||
      isLoadingStatuses ||
      isLoadingOrgStatuses ||
      isLoadingEmailTemplates,
  };
};
